/**
 * Single-flight refresh (FRONTEND_PLAN §7.1 #5).
 *
 * ★ Reusing a refresh token revokes the ENTIRE family (spec §5.1). Two concurrent refreshes — the
 *   natural result of two 401s arriving together — would therefore log the user out. Every caller
 *   awaits the same in-flight promise; the cookie is only ever presented once per rotation.
 */
import type { SessionResponse } from '@/api/types'
import { ApiError, codeForStatus, isErrorEnvelope } from '@/api/errors'
import { isSuccessEnvelope } from '@/api/envelope'

export type RefreshOutcome =
  | { readonly kind: 'ok'; readonly session: SessionResponse }
  /** No usable cookie (missing / unknown / past expiry). A normal re-login. */
  | { readonly kind: 'unauthenticated'; readonly code: 'TOKEN_INVALID' | 'TOKEN_EXPIRED' }
  /** A revoked token was replayed — hard logout, surface a security notice, never retry. */
  | { readonly kind: 'replay' }
  /** Server/network trouble; the current access token may still be valid. */
  | { readonly kind: 'error'; readonly error: ApiError }

export interface RefresherDeps {
  readonly apiBase: string
  readonly fetch: typeof fetch
  readonly onOutcome?: (outcome: RefreshOutcome) => void
}

export interface Refresher {
  /** Resolves for every concurrent caller with the same outcome. Never rejects. */
  refresh: () => Promise<RefreshOutcome>
  /** True while a refresh is in flight. */
  readonly inFlight: () => boolean
}

export function createRefresher(deps: RefresherDeps): Refresher {
  let pending: Promise<RefreshOutcome> | null = null

  async function run(): Promise<RefreshOutcome> {
    let response: Response
    try {
      response = await deps.fetch(`${deps.apiBase}/auth/refresh`, {
        method: 'POST',
        // The refresh token is the httpOnly cookie; nothing else is sent. No bearer on purpose.
        credentials: 'include',
        headers: { accept: 'application/json' },
      })
    } catch (cause) {
      return {
        kind: 'error',
        error: new ApiError({
          kind: 'network',
          status: 0,
          code: 'SERVICE_UNAVAILABLE',
          message: 'Network error during refresh',
          cause,
        }),
      }
    }

    const body: unknown = await response.json().catch(() => undefined)
    const requestId = response.headers.get('x-request-id') ?? undefined

    if (response.ok && isSuccessEnvelope(body)) {
      return { kind: 'ok', session: body.data as SessionResponse }
    }

    if (isErrorEnvelope(body)) {
      const { code, message, details } = body.error
      if (code === 'TOKEN_REPLAY_DETECTED') return { kind: 'replay' }
      if (code === 'TOKEN_INVALID' || code === 'TOKEN_EXPIRED')
        return { kind: 'unauthenticated', code }
      return {
        kind: 'error',
        error: new ApiError({
          kind: 'api',
          status: response.status,
          code,
          message,
          details,
          requestId: body.request_id ?? requestId,
        }),
      }
    }

    return {
      kind: 'error',
      error: new ApiError({
        kind: 'transport',
        status: response.status,
        code: codeForStatus(response.status),
        message: `Refresh failed with HTTP ${String(response.status)}`,
        requestId,
      }),
    }
  }

  return {
    refresh: () => {
      pending ??= run()
        .then((outcome) => {
          deps.onOutcome?.(outcome)
          return outcome
        })
        .finally(() => {
          pending = null
        })
      return pending
    },
    inFlight: () => pending !== null,
  }
}
